import { useState } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import * as Yup from 'yup';
import { IconCreditCard } from '@tabler/icons-react';
import { toast } from 'react-hot-toast';

import Spinner from 'components/Spinner';
import {
  RenderBankLogo,
  RenderBankLogoName,
} from 'components/RenderBankPaymentMethod';
import OtpInput from '../OTPInput';
import { baseClass, errorClass, normalClass } from 'lib/costumeStyle';
import { useBindCardStore } from 'stores/useBindCardStore';
import {
  FormDirectDebitBTNType,
  BindCardValues,
  PaymentResultData,
} from 'pages/types';

import { post } from 'network/api';
import { CardInfo, CardType, detectCardType } from 'lib/card-switcher';

const paySchema = Yup.object({
  bankCardNo: Yup.string()
    .required('Card number is required')
    .matches(/^(\d{4}\s){3}\d{4}$/, 'Card number must be 16 digits')
    .test('card-number-validity', 'Card Number is not valid', function (value) {
      if (!value) return false;
      return detectCardType(value).type !== 'UNKNOWN';
    }),

  expiryDate: Yup.string()
    .required('Expiry date is required')
    .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Expiry date must be in MM/YY format')
    .test(
      'is-future-date',
      'Expiry date must be in the future',
      function (value) {
        if (!value) return false;

        const [monthStr, yearStr] = value.split('/');
        const month = parseInt(monthStr, 10);
        const year = parseInt(yearStr, 10);

        if (isNaN(month) || isNaN(year)) return false;

        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear() % 100;

        return (
          year > currentYear || (year === currentYear && month >= currentMonth)
        );
      }
    ),

  customer_phone_no: Yup.string()
    .required('Phone number is required')
    .matches(/^[0-9]{8,15}$/, 'Invalid phone number'),

  identificationNo: Yup.string().required('Identification Number is required'),

  cvv: Yup.string()
    .required('CVV is required')
    .matches(/^\d{3,4}$/, 'CVV must be 3 or 4 digits'),
});

const FormDirectDebitBTN: React.FC<FormDirectDebitBTNType> = ({
  items,
  settings,
  accountCC,
  setAccountCC,
  setPickPayment,
  setDataPayment,
}) => {
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { id } = query as { id?: string };
  const [step, setStep] = useState<
    'form' | '1st-otp-binding' | '2nd-create-payment' | '3rd-confirm-otp'
  >('form');
  const [originalPartnerReferenceNo, setOriginalPartnerReferenceNo] =
    useState<string>('');
  const [originalReferenceNo, setOriginalReferenceNo] = useState<string>('');
  const [h2hWebRedirectUrl, setH2hWebRedirectUrl] = useState<string>('');
  const [customerPhoneNum, setCustomerPhoneNum] = useState<string>('');
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [temporaryDataPayment, setTemporaryDataPayment] =
    useState<PaymentResultData | null>(null);
  const [cardResponse, setCardResponse] = useState<CardInfo | null>(null);
  const { otpData } = useBindCardStore();

  const formatCardNumber = (value: string) => {
    const numericValue = value.replace(/\D/g, '').slice(0, 16);
    return numericValue.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiryDate = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = numericValue.slice(0, 4);

    if (formattedValue.length > 2) {
      return formattedValue.slice(0, 2) + '/' + formattedValue.slice(2);
    } else {
      return formattedValue;
    }
  };

  const onSubmitSuccessPayment = async (confirmOtp: string, type: string) => {
    // LAST STEP TO POST OTP PAYMENT BEFORE REDIRECT TO H2H PAYMENT URL
    if (confirmOtp === '999999') {
      toast.error('Invalid OTP!');
      return;
    }

    if (confirmOtp === '000000') {
      toast.error('OTP expired');
      return;
    }

    const payloadSubmitOtpBind = {
      originalPartnerReferenceNo:
        otpData?.otp_response.originalPartnerReferenceNo,
      originalReferenceNo: otpData?.otp_response.originalReferenceNo,
      otp: confirmOtp,
      type: type,
    };

    await post(
      `/checkout/v1/payment/card-bind/otp?invoice_number=${id}`,
      payloadSubmitOtpBind
    )
      .then(async () => {
        setPickPayment(true);
        if (temporaryDataPayment) setDataPayment(temporaryDataPayment);

        if (h2hWebRedirectUrl) {
          if (h2hWebRedirectUrl.startsWith('http'))
            window.open(h2hWebRedirectUrl, '_blank');
          else window.location.href = h2hWebRedirectUrl;
        }
      })
      .catch((err) => {
        toast.error(err.detail);
        setLoadingSubmit(false);
      });
  };

  const onCreateBindCard = async (value: BindCardValues) => {
    // FIRST STEP TO CREATE BIND CARD
    const payload = {
      invoice_number: value.invoice_number,
      customer_phone_no: value.customer_phone_no,
      card_data: {
        bankCardNo: value.bankCardNo.replace(/\s/g, ''),
        bankCardType: value.bankCardType,
        expiryDate: value.expiryDate.replace(/\//g, ''),
        identificationNo: value.identificationNo,
        identificationType: value.identificationType,
        paymentMethod: value.paymentMethod,
        paymentChannel: value.paymentChannel,
      },
    };

    setLoadingSubmit(true);
    await post(`/checkout/v1/payment/card-bind`, payload)
      .then((res) => {
        const { data } = res;
        setLoadingSubmit(false);
        const last4Digits = value.bankCardNo.replace(/\s/g, '').slice(-4);
        setAccountCC?.(`BTN •••• ${last4Digits}`);
        setOriginalPartnerReferenceNo(data.partnerReferenceNo);
        setOriginalReferenceNo(data.referenceNo);
        setCustomerPhoneNum(value.customer_phone_no);
        setStep('1st-otp-binding');
      })
      .catch((err) => {
        toast.error(err.detail);
        setLoadingSubmit(false);
      });
  };

  const createPayment = async () => {
    // THIRD STEP TO CREATE PAYMENT
    if (!items) {
      toast.error('Payment type is not selected!');
      return;
    }

    const payload = {
      type: items.type ? items.type : '',
      service_id: items.service_id ? items.service_id : '',
      customer_no: customerPhoneNum,
      payment_method_id: items.id ? items.id : 0,
    };

    await post(`/checkout/v1/payment-method?invoice_number=${id}`, payload)
      .then((res) => {
        const { data } = res;
        setLoadingSubmit(false);
        setTemporaryDataPayment({
          type: data.type ? data?.type : '-',
          bank: data.bank_info ? data?.bank_info?.bank_name : '-',
          va_number: data.bank_info ? data?.bank_info?.account_number : '-',
          account_name: data.virtual_account_name
            ? data?.virtual_account_name
            : '-',
          qr_content: data.qr_content ? data?.qr_content : '-',
          host_to_host_payment_info: data.h2h_payment_info || {
            web_redirect_url: undefined,
          },
        });

        if (data.h2h_payment_info.web_redirect_url) {
          setH2hWebRedirectUrl(data.h2h_payment_info.web_redirect_url);
        }
        setStep('3rd-confirm-otp');
      })
      .catch((err) => {
        toast.error(err.detail);
        setLoadingSubmit(false);
      });
  };

  const onSubmitOTP = async (
    otp: string,
    originalPartnerReferenceNo: string,
    originalReferenceNo: string,
    type: string
  ) => {
    // SECOND STEP TO SUBMIT OTP BINDING
    const payloadSubmitOtpBind = {
      originalPartnerReferenceNo: originalPartnerReferenceNo,
      originalReferenceNo: originalReferenceNo,
      otp: otp,
      type: type,
    };

    setLoadingSubmit(false);
    await post(
      `/checkout/v1/payment/card-bind/otp?invoice_number=${id}`,
      payloadSubmitOtpBind
    )
      .then((res) => {
        const { data } = res;
        useBindCardStore.getState().setOtpData(data);
        setStep('2nd-create-payment');
      })
      .catch((err) => {
        toast.error(err.detail);
        setLoadingSubmit(false);
      });
  };

  if (step === '3rd-confirm-otp') {
    return (
      <OtpInput
        onSubmit={(otp: string) => {
          onSubmitSuccessPayment(otp, 'PAYMENT');
        }}
        color={settings.button_color}
        textColor={settings.button_text_color}
        title="Please enter the 6-digit OTP to confirm your payment. This is your
              final confirmation step."
        btnText="PAY"
      />
    );
  }

  if (step === '2nd-create-payment') {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <RenderBankLogoName
            name={`${accountCC} BTN`}
            title={'DD'}
            key={0}
            idx={0}
          />
        </div>
        <button
          style={{
            backgroundColor: settings.button_color,
            color: settings.button_text_color,
          }}
          disabled={loadingSubmit}
          onClick={() => createPayment()}
          className={`mt-2 w-full flex flex-row items-center gap-2 justify-center rounded-md border border-transparent ${
            loadingSubmit ? 'bg-gray-200 hover:bg-gray-400' : ''
          } px-4 py-2.5 text-base font-medium text-white shadow-sm  focus:outline-none`}
        >
          {loadingSubmit ? <Spinner /> : 'PAY'}
        </button>
      </div>
    );
  }

  if (step === '1st-otp-binding') {
    return (
      <OtpInput
        onSubmit={(otp: string) => {
          onSubmitOTP(
            otp,
            originalPartnerReferenceNo,
            originalReferenceNo,
            'BINDING'
          );
        }}
        color={settings.button_color}
        textColor={settings.button_text_color}
        title="We’ve sent a 6-digit verification code to your phone or email to
                verify your credit card. Please enter the code below to continue."
        btnText="SUBMIT OTP"
      />
    );
  }

  return (
    <Formik
      initialValues={{
        invoice_number: id,
        customer_phone_no: '',
        bankCardNo: '',
        bankCardType: 'D',
        expiryDate: '',
        identificationNo: '',
        identificationType: '02',
        paymentMethod: 'DIRECT_DEBIT',
        paymentChannel: 'BTN',
        cvv: '',
      }}
      validationSchema={paySchema}
      onSubmit={(values) => {
        onCreateBindCard(values);
      }}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form className="grid grid-cols-12 gap-x-2 gap-y-4">
          {/* Name */}
          <div className="col-span-full">
            <label
              htmlFor="identificationNo"
              className="block text-sm font-medium"
            >
              Identification Number (KTP)
            </label>
            <div className="relative">
              <Field
                name="identificationNo"
                type="text"
                as="input"
                className={`input-costume ${baseClass} ${errors.identificationNo && touched.identificationNo ? errorClass : normalClass} pr-12`}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const onlyLetters = e.target.value.replace(/\D/g, '');
                  setFieldValue('identificationNo', onlyLetters);
                }}
                style={
                  {
                    '--button-color': settings.button_color,
                  } as React.CSSProperties
                }
              />
              <div className="absolute right-3 top-1.5 flex justify-center items-center">
                <span>KTP</span>
              </div>
            </div>
            <ErrorMessage
              name="identificationNo"
              component="div"
              className="text-red-500 text-sm mt-1"
            />
          </div>

          {/* Phone number */}
          <div className="col-span-full">
            <label
              htmlFor="customer_phone_no"
              className="block text-sm font-medium"
            >
              Phone Number
            </label>
            <Field
              name="customer_phone_no"
              type="text"
              className={`input-costume ${baseClass} ${errors.customer_phone_no && touched.customer_phone_no ? errorClass : normalClass}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const numeric = e.target.value.replace(/\D/g, '');
                setFieldValue('customer_phone_no', numeric);
              }}
              style={
                {
                  '--button-color': settings.button_color,
                } as React.CSSProperties
              }
            />
            <ErrorMessage
              name="customer_phone_no"
              component="div"
              className="text-red-500 text-sm mt-1"
            />
          </div>

          {/* Card Number */}
          <div className="col-span-full">
            <div className="flex justify-between items-center">
              <label htmlFor="bankCardNo" className="block text-sm font-medium">
                Card Number
              </label>
              {cardResponse &&
                cardResponse.type !== CardType.INVALID &&
                cardResponse.type !== CardType.UNKNOWN && (
                  <RenderBankLogo
                    title={`${cardResponse.type.toLowerCase()}-card`}
                    height="h-10"
                  />
                )}
            </div>
            <Field
              name="bankCardNo"
              type="text"
              className={`input-costume ${baseClass} ${errors.bankCardNo && touched.bankCardNo ? errorClass : normalClass}`}
              value={formatCardNumber(values.bankCardNo)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const formatted = formatCardNumber(e.target.value);
                setCardResponse(detectCardType(e.target.value));
                setFieldValue('bankCardNo', formatted);
              }}
              style={
                {
                  '--button-color': settings.button_color,
                } as React.CSSProperties
              }
            />
            <ErrorMessage
              name="bankCardNo"
              component="div"
              className="text-red-500 text-sm mt-1"
            />
          </div>

          {/* Expiry + CVV */}
          <div className="col-span-6">
            <label htmlFor="expiryDate" className="block text-sm font-medium">
              Expiry Date (MM/YY)
            </label>
            <Field
              name="expiryDate"
              type="text"
              className={`input-costume ${baseClass} ${errors.expiryDate && touched.expiryDate ? errorClass : normalClass}`}
              value={formatExpiryDate(values.expiryDate)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const formatted = formatExpiryDate(e.target.value);
                setFieldValue('expiryDate', formatted);
              }}
              style={
                {
                  '--button-color': settings.button_color,
                } as React.CSSProperties
              }
            />
            <ErrorMessage
              name="expiryDate"
              component="div"
              className="text-red-500 text-sm mt-1"
            />
          </div>
          <div className="col-span-6">
            <label htmlFor="cvv" className="block text-sm font-medium">
              CVV
            </label>
            <div className="relative">
              <Field
                name="cvv"
                type="password"
                className={`input-costume ${baseClass} ${errors.cvv && touched.cvv ? errorClass : normalClass}`}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const numeric = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setFieldValue('cvv', numeric);
                }}
                style={
                  {
                    '--button-color': settings.button_color,
                  } as React.CSSProperties
                }
              />
              <IconCreditCard className="absolute top-1.5 right-2 w-6 h-6" />
            </div>
            <ErrorMessage
              name="cvv"
              component="div"
              className="text-red-500 text-sm mt-1"
            />
          </div>

          <div className="col-span-full">
            <button
              type="submit"
              disabled={loadingSubmit}
              style={{
                backgroundColor: loadingSubmit
                  ? '#f9fafb'
                  : settings.button_color,
                color: loadingSubmit ? '#6b7280' : settings.button_text_color,
                cursor: loadingSubmit ? 'not-allowed' : 'pointer',
                borderColor: loadingSubmit ? '#e5e7eb' : 'transparent',
              }}
              className="w-full flex flex-row items-center gap-2 justify-center rounded-md border px-4 py-2.5 text-base font-medium text-white shadow-sm focus:outline-none"
            >
              {loadingSubmit ? <Spinner /> : 'ADD CARD'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default FormDirectDebitBTN;
